/**
 * Promo code discount types and their nested property shapes.
 * Each type has its own property object on the offer document.
 */

export const DISCOUNT_TYPE_KEYS = [
  'percentage',
  'fixed_amount',
  'free_addon',
  'first_order',
  'cashback',
  'bogo',
];

export const DISCOUNT_TYPES = {
  percentage: {
    key: 'percentage',
    label: 'Percentage Off',
    property: 'percentage',
    shape: { percent: null, cap: null },
    getTableDisplay: (promo) => {
      const p = promo?.percentage;
      if (!p || (p.percent == null && p.cap == null)) return '--';
      return `${p.percent ?? '--'}% (max ${p.cap ?? '--'} AED)`;
    },
  },
  fixed_amount: {
    key: 'fixed_amount',
    label: 'Fixed Amount Off',
    property: 'fixedAmount',
    shape: { amount: null },
    getTableDisplay: (promo) => {
      const a = promo?.fixedAmount?.amount;
      return a != null ? `${a} AED` : '--';
    },
  },
  free_addon: {
    key: 'free_addon',
    label: 'Free Add-on',
    property: 'freeAddon',
    shape: { addonId: null, type: 'percent', value: null, cap: null },
    getTableDisplay: (promo, options = {}) => {
      const fa = promo?.freeAddon;
      if (!fa?.addonId || fa.value == null) return '--';
      const label = options.addonLabel || fa.addonId;
      if (fa.type === 'fixed') return `${label}: ${fa.value} AED off`;
      return `${label}: ${fa.value}%${fa.cap != null ? ` (max ${fa.cap} AED)` : ''} off`;
    },
  },
  first_order: {
    key: 'first_order',
    label: 'First / X Order',
    property: 'firstOrder',
    shape: { type: 'percent', value: null, cap: null, orderNumber: 1 },
    getTableDisplay: (promo) => {
      const o = promo?.firstOrder;
      if (!o || o.value == null) return '--';
      const ord = o.orderNumber ?? 1;
      const ordLabel = ord === 1 ? '1st' : ord === 2 ? '1st–2nd' : ord === 3 ? '1st–3rd' : `1st–${ord}th`;
      if (o.type === 'fixed') return `${ordLabel} order: ${o.value} AED off`;
      return `${ordLabel} order: ${o.value}%${o.cap != null ? ` (max ${o.cap} AED)` : ''} off`;
    },
  },
  cashback: {
    key: 'cashback',
    label: 'Voucher',
    property: 'cashback',
    shape: { type: 'percent', value: null, cap: null, expiryDays: null, numberOfSplits: null },
    getTableDisplay: (promo) => {
      const c = promo?.cashback;
      if (!c || c.value == null) return '--';
      let s = c.type === 'fixed' ? `${c.value} AED voucher` : `${c.value}%${c.cap != null ? ` (max ${c.cap} AED)` : ''} voucher`;
      if (c.expiryDays != null) s += ` · ${c.expiryDays}d`;
      const splits = Number(c.numberOfSplits);
      if (Number.isFinite(splits) && splits > 1) s += ` · ${Math.floor(splits)}× wallet credits`;
      return s;
    },
  },
  bogo: {
    key: 'bogo',
    label: 'BOGO (Buy One Get One)',
    property: 'bogo',
    shape: { percent: null, cap: null, expiryDays: null, serviceId: null, bogoWashingTypes: null },
    getTableDisplay: (promo, options = {}) => {
      const b = promo?.bogo;
      if (!b || b.percent == null) return '--';
      let s = `${b.percent}% off next service`;
      if (b.cap != null) s += ` (max ${b.cap} AED)`;
      if (b.expiryDays != null) s += ` · ${b.expiryDays}d`;
      if (b.serviceId && options.serviceLabel) s += ` · ${options.serviceLabel}`;
      const wt = b?.bogoWashingTypes ?? b?.washingTypes;
      if (wt && wt.length) {
        const labels = wt.map(v => {
          if (v?.key === 'pressureWash' || v === 'pressureWash') return 'Pressure Wash';
          if (v?.key === 'waterlessWash' || v === 'waterlessWash') return 'Waterless Wash';
          if (v?.key === 'steamWash' || v === 'steamWash') return 'Steam Wash';
          return v?.name || v;
        });
        s += ` · ${labels.join(', ')}`;
      }
      return s;
    },
  },
};

/**
 * Get the property key (e.g. 'percentage', 'cashback') for a discount type.
 */
export function getPropertyKey(discountType) {
  return DISCOUNT_TYPES[discountType]?.property ?? 'percentage';
}

/**
 * Get table display string for a promo based on its discountType.
 */
export function getPromoTableDisplay(promo, options = {}) {
  const type = promo?.discountType ?? 'percentage';
  const config = DISCOUNT_TYPES[type];
  if (!config) return '--';
  return config.getTableDisplay(promo, options);
}

/**
 * Normalize legacy offer (flat percent/cap) to nested percentage shape.
 * Also migrates old firstOrder shape (percent, cap, amount) to new (type, value, cap).
 */
export function normalizeLegacyOffer(offer) {
  if (!offer) return offer;
  let result = { ...offer };

  // Legacy flat percent/cap at root
  if (offer.discountType == null) {
    const percent = offer.percent;
    const cap = offer.cap;
    if (percent != null || cap != null) {
      result.discountType = 'percentage';
      result.percentage = { percent: percent ?? null, cap: cap ?? null };
      return result;
    }
  }

  // Legacy firstOrder shape (percent, cap, amount) -> new (type, value, cap, orderNumber)
  const fo = offer.firstOrder;
  if (fo && (fo.percent != null || fo.amount != null) && fo.type == null) {
    if (fo.amount != null) {
      result.firstOrder = { type: 'fixed', value: fo.amount, cap: null, orderNumber: fo.orderNumber ?? 1 };
    } else {
      result.firstOrder = { type: 'percent', value: fo.percent ?? null, cap: fo.cap ?? null, orderNumber: fo.orderNumber ?? 1 };
    }
  }

  // free_addon -> { addonId, type, value, cap } (same pattern as firstOrder)
  if (result.discountType === 'free_addon' && result.freeAddon != null) {
    const fa = result.freeAddon;
    const hasNewShape = fa.type === 'percent' || fa.type === 'fixed';
    if (!hasNewShape) {
      if (fa.reductionType === 'percent') {
        result.freeAddon = {
          addonId: fa.addonId ?? null,
          type: 'percent',
          value: fa.percent != null ? fa.percent : null,
          cap: fa.cap ?? null,
        };
      } else if (fa.reductionType === 'fixed') {
        result.freeAddon = {
          addonId: fa.addonId ?? null,
          type: 'fixed',
          value: fa.amount != null ? fa.amount : null,
          cap: null,
        };
      } else {
        // legacy: addonId only, or reductionType 'full' / missing → 100% off add-on
        result.freeAddon = {
          addonId: fa.addonId ?? null,
          type: 'percent',
          value: 100,
          cap: null,
        };
      }
    }
  }

  return result;
}
